import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { actionIcons } from '../utils/iconMap';

const SparklesIcon = actionIcons.sparkles;
import { RARITY_LABELS_TR, RARITY_UI, revealRarityKey } from '../utils/collectionUi';
import { pickActivityThumbnail, feedAccentClasses, formatRelativeTimeTr } from '../utils/activityFeedUi';
import {
  pickFeaturedHighlightEvent,
  featuredHighlightHeading,
  featuredHighlightSubline,
  featuredShowRarityBadge,
} from '../utils/featuredHighlight';
import FeedEventThumb from './FeedEventThumb';
import { FeedEventIcon } from '../utils/iconMap';

/**
 * Dashboard üstü — highlights’tan seçilen tek premium olay (feed sistemini değiştirmez).
 * sharedHighlights: null = yükleniyor (kart gösterme), dizi = adaylar
 */
export default function FeaturedHighlightCard({ sharedHighlights }) {
  const featured = useMemo(
    () => (Array.isArray(sharedHighlights) ? pickFeaturedHighlightEvent(sharedHighlights) : null),
    [sharedHighlights],
  );

  if (sharedHighlights === null || sharedHighlights === undefined) return null;
  if (!featured) return null;

  const rk = revealRarityKey(featured.asset_rarity);
  const ui = RARITY_UI[rk] || RARITY_UI.common;
  const accent = feedAccentClasses(featured);
  const thumbUrl = pickActivityThumbnail(featured);
  const heading = featuredHighlightHeading(featured);
  const subline = featuredHighlightSubline(featured);
  const showRarity = featuredShowRarityBadge(featured);

  const isRareDropTop =
    featured.event_type === 'rare_drop' && (rk === 'legendary' || rk === 'mythic');
  const heroGlow =
    rk === 'mythic' && featured.event_type === 'rare_drop'
      ? 'featured-hero-glow-mythic'
      : rk === 'legendary' && featured.event_type === 'rare_drop'
        ? 'featured-hero-glow-legendary'
        : '';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className={`relative overflow-hidden rounded-2xl border bg-black/50 ${accent.frame} ${accent.glow} ${heroGlow}`}
    >
      {isRareDropTop && (
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07] bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-200/40 via-transparent to-transparent"
          aria-hidden
        />
      )}

      <div className="relative z-10 flex flex-col sm:flex-row gap-4 p-4 sm:p-5 items-stretch">
        <FeedEventThumb
          url={thumbUrl}
          size="lg"
          className={`border-white/20 shadow-inner ${featured.event_type === 'rare_drop' ? 'ring-1 ring-white/10' : ''}`}
        >
          <div
            className={`flex h-full w-full items-center justify-center rounded-xl border bg-gradient-to-br from-black/70 to-black/40 ${ui.chip}`}
          >
            <FeedEventIcon eventType={featured.event_type} size="large" />
          </div>
        </FeedEventThumb>

        <div className="min-w-0 flex-1 flex flex-col justify-center gap-2">
          <div className="flex flex-wrap items-center gap-2 gap-y-1">
            <span className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-semibold">Öne çıkan</span>
            {showRarity && (
              <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${ui.badge}`}>
                {RARITY_LABELS_TR[rk]}
              </span>
            )}
          </div>
          <h2
            className="text-lg sm:text-xl font-bold text-white leading-tight"
            style={{ fontFamily: 'Cinzel, serif' }}
          >
            {heading}
          </h2>
          <p className="text-sm text-gray-200/95 leading-snug line-clamp-3">{subline}</p>
          <div className="flex items-center justify-between gap-2 pt-1">
            <time className="text-[11px] text-gray-500 tabular-nums" dateTime={featured.created_at}>
              {formatRelativeTimeTr(featured.created_at)}
            </time>
            <SparklesIcon className="w-4 h-4 text-violet-400/50 shrink-0 hidden sm:block" aria-hidden />
          </div>
        </div>
      </div>
    </motion.div>
  );
}
